<template>
  <v-container fluid class="pa-6">
    <!-- KPI Cards Section -->
    <v-card class="overview-card elevation-0 mb-6" outlined>
      <v-card-text class="pa-4">
        <div class="overview-header">
          <h3 class="overview-title">Resumen de Compras</h3>
          <v-chip small :color="getOverallHealthColor()" text-color="white" class="ml-2">
            {{ getOverallStatus() }}
          </v-chip>
        </div>

        <!-- KPIs en formato compacto horizontal -->
        <v-row class="mt-3" dense>
          <v-col cols="12" sm="6" md="3" v-for="kpi in primaryKpis" :key="kpi.key">
            <div class="compact-kpi">
              <div class="kpi-header">
                <v-icon :color="kpi.color" size="20" class="mr-2">{{ kpi.icon }}</v-icon>
                <span class="kpi-label">{{ kpi.label }}</span>
              </div>
              <div class="kpi-main-value">{{ kpi.value }}</div>
              <div class="kpi-subtitle" v-if="kpi.subtitle">{{ kpi.subtitle }}</div>
            </div>
          </v-col>
        </v-row>

        <!-- KPIs secundarios colapsables -->
        <v-expand-transition>
          <div v-show="showSecondaryKpis">
            <v-divider class="my-3"></v-divider>
            <v-row dense>
              <v-col cols="12" sm="6" md="3" v-for="kpi in secondaryKpis" :key="kpi.key">
                <div class="compact-kpi">
                  <div class="kpi-header">
                    <v-icon :color="kpi.color" size="18" class="mr-2">{{
                      kpi.icon
                    }}</v-icon>
                    <span class="kpi-label small">{{ kpi.label }}</span>
                  </div>
                  <div class="kpi-value-small">{{ kpi.value }}</div>
                  <div class="kpi-subtitle small" v-if="kpi.subtitle">
                    {{ kpi.subtitle }}
                  </div>
                </div>
              </v-col>
            </v-row>
          </div>
        </v-expand-transition>

        <!-- Toggle para KPIs adicionales -->
        <div class="text-center mt-3">
          <v-btn
            text
            small
            @click="showSecondaryKpis = !showSecondaryKpis"
            color="primary"
          >
            <v-icon small class="mr-1">
              {{ showSecondaryKpis ? "mdi-chevron-up" : "mdi-chevron-down" }}
            </v-icon>
            {{ showSecondaryKpis ? "Menos detalles" : "Más detalles" }}
          </v-btn>
        </div>
      </v-card-text>
    </v-card>

    <!-- Main Content Card -->
    <v-card class="main-card elevation-0" outlined>
      <!-- Header -->
      <v-card-title class="pa-0">
        <v-toolbar flat class="minimal-toolbar">
          <h2 class="section-title">Ingresar Compra</h2>
          <v-spacer></v-spacer>
        </v-toolbar>
      </v-card-title>

      <!-- Search Form -->
      <v-card-text class="filter-section">
        <v-form v-model="searchvalid" ref="form" @submit="get_report">
          <v-row align="center">
            <v-col cols="12" md="4">
              <v-select
                v-model="supplier"
                :items="supplier_lst"
                item-value="codigo"
                item-text="nombre"
                label="Proveedor"
                prepend-inner-icon="mdi-domain"
                clearable
                outlined
                dense
                :rules="f_required"
                hide-details
                class="minimal-input"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                v-model="dateFrom"
                label="Fecha desde"
                prepend-inner-icon="mdi-calendar"
                outlined
                dense
                type="date"
                :rules="f_required"
                hide-details
                class="minimal-input"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                v-model="dateTo"
                label="Fecha hasta"
                prepend-inner-icon="mdi-calendar"
                outlined
                dense
                type="date"
                :rules="f_required"
                hide-details
                class="minimal-input"
              />
            </v-col>

            <v-col cols="12" md="2">
              <v-btn
                color="primary"
                ref="submitbtn"
                type="submit"
                block
                large
                :loading="loading_status"
              >
                <v-icon left>mdi-magnify</v-icon>
                Buscar
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <!-- Data Table -->
      <v-data-table
        :headers="minimalHeaders"
        :items="items"
        :items-per-page="15"
        item-key="id"
        sort-by="order_date"
        sort-desc
        class="minimal-table"
        :loading="loading_status"
        :footer-props="{
          'items-per-page-options': [15, 30, 50, 100],
        }"
      >
        <!-- Custom slots for formatted data -->
        <template v-slot:item.order_info="{ item }">
          <div class="order-info">
            <div class="order-main">Orden #{{ item.order_id }}</div>
            <div class="order-meta">
              {{ formatDate(item.order_date) }}
            </div>
          </div>
        </template>

        <template v-slot:item.financial="{ item }">
          <div class="financial-info">
            <div class="financial-value">${{ formatNumber(item.ordered_val) }}</div>
            <div class="financial-cost">
              Costo: ${{ formatNumber(item.ordered_cost) }}
            </div>
          </div>
        </template>

        <template v-slot:item.status="{ item }">
          <div class="status-info">
            <v-progress-linear
              :value="getDeliveryProgress(item)"
              :color="getProgressColor(item)"
              height="6"
              rounded
              class="mb-2"
            />
            <div class="status-text">
              {{ item.delivered_qty }} / {{ item.ordered_qty }}
            </div>
          </div>
        </template>

        <template v-slot:item.order_age="{ item }">
          <v-chip
            small
            :color="getAgeColor(item.order_age)"
            :text-color="getAgeTextColor(item.order_age)"
            class="age-chip"
          >
            {{ item.order_age }} días
          </v-chip>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-btn
            icon
            small
            @click="showDoc(item)"
            :disabled="item.delivered_qty >= item.ordered_qty"
          >
            <v-icon small>mdi-package-down</v-icon>
          </v-btn>
        </template>

        <!-- Empty state -->
        <template v-slot:no-data>
          <v-container class="text-center py-12">
            <v-icon size="64" color="grey lighten-2">mdi-package-variant-closed</v-icon>
            <h3 class="mt-4 grey--text text--lighten-1">
              No hay órdenes en el período seleccionado
            </h3>
            <p class="grey--text">Ajusta los filtros para ver más resultados</p>
          </v-container>
        </template>
      </v-data-table>
    </v-card>

    <!-- Order Items Dialog -->
    <v-dialog v-model="order_dialog" max-width="900px" scrollable>
      <v-card class="elevation-0">
        <v-toolbar flat class="minimal-toolbar">
          <h3 class="dialog-title">
            Productos de la Orden #{{ docId }}
            <span class="ml-2 text-caption grey--text">{{ formatDate(docDate) }}</span>
          </h3>
          <v-spacer></v-spacer>
          <v-btn icon @click="order_dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <!-- Order Summary -->
        <v-card-text class="pa-0">
          <v-alert v-if="error_form" type="error" text class="ma-4">
            <strong>Atención:</strong> Existen productos con diferencias de precio en el
            inventario
          </v-alert>

          <v-row class="order-summary pa-4" v-if="orderItems.length > 0">
            <v-col cols="6" sm="3">
              <div class="summary-item">
                <div class="summary-label">Productos</div>
                <div class="summary-value">{{ orderItems.length }}</div>
              </div>
            </v-col>
            <v-col cols="6" sm="3">
              <div class="summary-item">
                <div class="summary-label">Unidades</div>
                <div class="summary-value">{{ orderTotalQuantity }}</div>
              </div>
            </v-col>
            <v-col cols="6" sm="3">
              <div class="summary-item">
                <div class="summary-label">Costo Total</div>
                <div class="summary-value">${{ orderTotalCost }}</div>
              </div>
            </v-col>
            <v-col cols="6" sm="3">
              <div class="summary-item">
                <div class="summary-label">Valor Total</div>
                <div class="summary-value">${{ orderTotalValue }}</div>
              </div>
            </v-col>
          </v-row>

          <!-- Products Table -->
          <v-data-table
            v-model="selected"
            :headers="minimalOrderHeaders"
            :items="orderItems"
            item-key="product_code"
            show-select
            disable-pagination
            hide-default-footer
            class="minimal-table order-items-table"
          >
            <template v-slot:item.nombre="{ item }">
              <div class="product-info">
                <div class="product-main">{{ item.nombre }}</div>
                <div class="product-variant">
                  {{ item.refe }} / {{ item.color }} / Talla: {{ item.size }}
                </div>
              </div>
            </template>

            <template v-slot:item.quantity="{ item }">
              <v-edit-dialog
                :return-value.sync="item.quantity"
                @save="save"
                save-text="Guardar"
                cancel-text="Cancelar"
                large
              >
                <div class="editable-quantity">
                  {{ item.quantity }}
                  <v-icon small class="ml-1">mdi-pencil</v-icon>
                </div>
                <template v-slot:input>
                  <v-text-field
                    v-model="item.quantity"
                    label="Cantidad"
                    type="number"
                    single-line
                    outlined
                    dense
                    autofocus
                  />
                </template>
              </v-edit-dialog>
            </template>

            <template v-slot:item.price="{ item }">
              <div class="price-info">
                <span :class="{ 'error--text': hasPriceError(item) }">
                  ${{ formatNumber(item.price) }}
                </span>
                <v-icon v-if="hasPriceError(item)" small color="error" class="ml-1">
                  mdi-alert-circle
                </v-icon>
              </div>
            </template>
          </v-data-table>
        </v-card-text>

        <!-- Actions -->
        <v-card-actions class="action-section pa-4" v-if="selected.length > 0">
          <v-form v-model="validIn" ref="formIn" @submit="StockIn" class="action-form">
            <v-row align="center">
              <v-col cols="12" sm="3">
                <div class="selected-info">
                  <div class="selected-label">Seleccionados</div>
                  <div class="selected-value">{{ SelectedCount }} unidades</div>
                </div>
              </v-col>
              <v-col cols="12" sm="3">
                <v-select
                  v-model="typeIn"
                  :items="opin"
                  item-value="codigo"
                  item-text="valor"
                  label="Tipo de ingreso"
                  outlined
                  dense
                  :rules="f_required"
                  hide-details
                />
              </v-col>
              <v-col cols="12" sm="3">
                <v-text-field
                  v-model="docRefIn"
                  label="Referencia del documento"
                  outlined
                  dense
                  :rules="f_required"
                  hide-details
                />
              </v-col>
              <v-col cols="12" sm="3">
                <v-btn
                  color="success"
                  block
                  large
                  type="submit"
                  :disabled="btnIN || error_form"
                  :loading="btnIN"
                >
                  <v-icon left>mdi-package-down</v-icon>
                  Ingresar al Inventario
                </v-btn>
              </v-col>
            </v-row>
          </v-form>
        </v-card-actions>

        <v-card-text v-else class="text-center py-8">
          <v-icon size="48" color="grey lighten-2">mdi-checkbox-blank-outline</v-icon>
          <p class="mt-4 grey--text">Selecciona los productos que deseas ingresar</p>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { webserver, getToday, getdays_ago } from "@/services/webserver.js";
import createDoc from "@/utils/create_doc.js";

export default {
  data() {
    return {
      showSecondaryKpis: false,
      // Original data
      loading_status: false,
      items: [],
      dialog: false,
      item: createDoc(),
      dateFrom: getdays_ago(-30),
      dateTo: getToday(),
      supplier: null,

      f_required: [(v) => !!v || "Requerido"],
      searchvalid: false,
      docId: null,
      docDate: null,
      orderItems: [],
      order_dialog: false,
      selected: [],
      opin: [],
      typeIn: null,
      docRefIn: null,
      validIn: false,
      SelectedCount: 0,
      error_form: false,
      errorOrder: false,
      timeout: 5000,
      btnIN: false,
      supplier_lst: [],

      // Minimalist headers
      minimalHeaders: [
        {
          text: "Orden",
          value: "order_info",
          sortable: false,
          width: "25%",
        },
        {
          text: "Valores",
          value: "financial",
          sortable: false,
          align: "end",
          width: "20%",
        },
        {
          text: "Estado de Entrega",
          value: "status",
          sortable: false,
          align: "center",
          width: "25%",
        },
        {
          text: "Antigüedad",
          value: "order_age",
          align: "center",
          width: "15%",
        },
        {
          text: "",
          value: "actions",
          sortable: false,
          align: "center",
          width: "10%",
        },
      ],

      minimalOrderHeaders: [
        {
          text: "Producto",
          value: "nombre",
          sortable: false,
          width: "40%",
        },
        {
          text: "Cantidad",
          value: "quantity",
          align: "center",
          width: "15%",
        },
        {
          text: "Costo",
          value: "order_cost",
          align: "end",
          width: "15%",
        },
        {
          text: "Precio Venta",
          value: "price",
          align: "end",
          width: "15%",
        },
      ],
    };
  },

  computed: {
    // KPIs organizados por prioridad
    primaryKpis() {
      return [
        {
          key: "pending",
          label: "Órdenes Pendientes",
          value: this.pendingOrdersCount,
          subtitle: `${this.pendingItemsCount} productos`,
          icon: "mdi-package-variant",
          color: "primary",
        },
        {
          key: "value",
          label: "Valor Total",
          value: `$${this.totalOrderValue}`,
          subtitle: `Costo: $${this.totalOrderCost}`,
          icon: "mdi-currency-usd",
          color: "success",
        },
        {
          key: "age",
          label: "Edad Promedio",
          value: `${this.averageOrderAge} días`,
          subtitle:
            this.criticalOrdersCount > 0
              ? `${this.criticalOrdersCount} críticas`
              : "En tiempo",
          icon: "mdi-clock-alert",
          color: this.criticalOrdersCount > 0 ? "error" : "warning",
        },
        {
          key: "fulfillment",
          label: "Cumplimiento",
          value: `${this.fulfillmentRate}%`,
          subtitle: `${this.formatNumber(this.totalDelivered)} / ${this.formatNumber(
            this.totalOrdered
          )}`,
          icon: "mdi-chart-line",
          color: this.fulfillmentRate >= 80 ? "success" : "warning",
        },
      ];
    },

    secondaryKpis() {
      return [
        {
          key: "margin",
          label: "Margen Promedio",
          value: `${this.averageMargin}%`,
          subtitle: "Precio vs Costo",
          icon: "mdi-calculator",
          color: "deep-purple",
        },
        {
          key: "pending_value",
          label: "Valor Pendiente",
          value: `$${this.pendingValue}`,
          subtitle: "Por recibir",
          icon: "mdi-truck-delivery",
          color: "orange",
        },
        {
          key: "suppliers",
          label: "Proveedores",
          value: this.activeSuppliers,
          subtitle: "Activos",
          icon: "mdi-domain",
          color: "teal",
        },
        {
          key: "overdue",
          label: "Vencidas",
          value: this.overdueOrders,
          subtitle: "Más de 45 días",
          icon: "mdi-alert-circle",
          color: this.overdueOrders > 0 ? "error" : "success",
        },
      ];
    },
    // KPIs calculations
    pendingOrdersCount() {
      return this.items.filter((item) => item.delivered_qty < item.ordered_qty).length;
    },

    pendingItemsCount() {
      return this.items.reduce((sum, item) => {
        const pending = item.ordered_qty - item.delivered_qty;
        return sum + (pending > 0 ? pending : 0);
      }, 0);
    },

    totalOrderValue() {
      const total = this.items.reduce(
        (sum, item) => sum + parseFloat(item.ordered_val),
        0
      );
      return this.formatNumber(total);
    },

    totalOrderCost() {
      const total = this.items.reduce(
        (sum, item) => sum + parseFloat(item.ordered_cost),
        0
      );
      return this.formatNumber(total);
    },

    averageOrderAge() {
      if (this.items.length === 0) return 0;
      const totalAge = this.items.reduce(
        (sum, item) => sum + parseFloat(item.order_age),
        0
      );
      return Math.round(totalAge / this.items.length);
    },

    criticalOrdersCount() {
      return this.items.filter((item) => item.order_age > 30).length;
    },

    totalOrdered() {
      return this.items.reduce((sum, item) => sum + parseFloat(item.ordered_qty), 0);
    },

    totalDelivered() {
      return this.items.reduce((sum, item) => sum + parseFloat(item.delivered_qty), 0);
    },

    fulfillmentRate() {
      if (this.totalOrdered === 0) return 0;
      return Math.round((this.totalDelivered / this.totalOrdered) * 100);
    },

    // Nuevos KPIs
    averageMargin() {
      if (this.items.length === 0) return 0;
      const totalValue = this.items.reduce(
        (sum, item) => sum + parseFloat(item.ordered_val),
        0
      );
      const totalCost = this.items.reduce(
        (sum, item) => sum + parseFloat(item.ordered_cost),
        0
      );
      if (totalCost === 0) return 0;
      return Math.round(((totalValue - totalCost) / totalCost) * 100);
    },

    pendingValue() {
      const pendingVal = this.items.reduce((sum, item) => {
        const deliveredRatio =
          item.ordered_qty > 0 ? item.delivered_qty / item.ordered_qty : 0;
        const pendingVal = item.ordered_val * (1 - deliveredRatio);
        return sum + pendingVal;
      }, 0);
      return this.formatNumber(Math.round(pendingVal));
    },

    activeSuppliers() {
      // Contar proveedores únicos basándose en el brand/proveedor de las órdenes
      const suppliers = new Set();
      this.items.forEach((item) => {
        // Asumiendo que hay un campo de proveedor en los items
        if (item.supplier || item.brand) {
          suppliers.add(item.supplier || item.brand);
        }
      });
      return suppliers.size || this.items.length > 0 ? 1 : 0;
    },

    overdueOrders() {
      return this.items.filter(
        (item) => item.order_age > 45 && item.delivered_qty < item.ordered_qty
      ).length;
    },

    // Order dialog calculations
    orderTotalQuantity() {
      return this.formatNumber(
        this.orderItems.reduce((sum, item) => sum + parseFloat(item.quantity), 0)
      );
    },

    orderTotalCost() {
      return this.formatNumber(
        this.orderItems.reduce((sum, item) => sum + parseFloat(item.order_cost), 0)
      );
    },

    orderTotalValue() {
      return this.formatNumber(
        this.orderItems.reduce((sum, item) => sum + parseFloat(item.price), 0)
      );
    },
  },

  mounted() {
    this.get_supplier();
    // Verificar que existan los settings antes de intentar ordenarlos
    if (window.settings && window.settings["OP-IN"]) {
      this.opin = window.settings["OP-IN"].sort(this.GetSortOrder("valor"));
    } else {
      console.warn("window.settings['OP-IN'] no está definido");
      this.opin = [];
    }
  },

  methods: {
    getOverallHealthColor() {
      if (this.criticalOrdersCount > 0 || this.overdueOrders > 0) return "error";
      if (this.fulfillmentRate < 70) return "warning";
      return "success";
    },

    getOverallStatus() {
      if (this.criticalOrdersCount > 0) return "Atención Requerida";
      if (this.fulfillmentRate >= 80) return "En Buen Estado";
      return "Revisar Proceso";
    },
    // Original methods
    save() {},
    cancel() {},
    open() {},
    close() {},

    timeBtn() {
      this.btnIN = true;
      setTimeout(() => {
        this.btnIN = false;
      }, 1000);
    },

    showDoc(item) {
      this.selected = [];
      this.docId = item.order_id;
      this.docDate = item.order_date;
      this.$refs.submitbtn.$el.click();
      this.order_dialog = true;
    },

    get_report(e) {
      e.preventDefault();
      this.$refs.form.validate();
      if (this.searchvalid) {
        this.loading_status = true;
        var qry = {
          store: window.profile.store_id,
          from: this.dateFrom,
          to: this.dateTo,
          order_id: this.docId,
          supplier: this.supplier,
        };
        console.log(qry);
        webserver("get_orders", qry, (data) => {
          console.log(data);
          if (this.docId) {
            this.error_form = false;
            data.forEach((item) => {
              if (
                parseInt(item.price) - parseInt(item.price_inv) !== 0 &&
                item.price_inv != 0
              ) {
                this.error_form = true;
              }
            });
            this.orderItems = data;
          } else {
            this.items = data;
          }
          this.docId = null;
          this.loading_status = false;
        });
      }
    },

    GetSortOrder(prop) {
      return function (a, b) {
        if (a[prop] > b[prop]) {
          return 1;
        } else if (a[prop] < b[prop]) {
          return -1;
        }
        return 0;
      };
    },

    StockIn(e) {
      e.preventDefault();
      this.$refs.formIn.validate();
      if (this.validIn) {
        this.btnIN = true;
        var qry = {
          store: window.store.store_id,
          issueDate: getToday(),
          user_id: window.profile.user_email,
          orderRef: this.typeIn + "-" + this.docRefIn,
          data: this.selected,
        };

        this.loading_status = true;
        webserver("order_in", qry, () => {
          this.typeIn = null;
          this.docRefIn = null;
          this.loading_status = false;
          this.order_dialog = false;
          this.$refs.submitbtn.$el.click();
          this.btnIN = false;
        });
      }
    },

    get_supplier() {
      this.loading_status = true;
      var qry = {
        store: window.profile.store_id,
        table: "proveedores",
      };
      qry.filters = [
        {
          field: "store",
          operator: "=",
          value: window.profile.store_id.substring(0, 6),
        },
      ];

      webserver("get_table", qry, (data) => {
        this.loading_status = false;
        this.supplier_lst = data;
      });
    },

    // New helper methods
    formatNumber(value) {
      return new Intl.NumberFormat("es-CO").format(Math.round(value));
    },

    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString("es-CO", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },

    getDeliveryProgress(item) {
      if (item.ordered_qty === 0) return 0;
      return (item.delivered_qty / item.ordered_qty) * 100;
    },

    getProgressColor(item) {
      const progress = this.getDeliveryProgress(item);
      if (progress === 100) return "success";
      if (progress > 50) return "warning";
      return "error";
    },

    getAgeColor(days) {
      if (days > 30) return "error";
      if (days > 15) return "warning";
      return "success";
    },

    getAgeTextColor() {
      return "white";
    },

    hasPriceError(item) {
      return parseInt(item.price) - parseInt(item.price_inv) !== 0 && item.price_inv != 0;
    },

    new_order() {
      if (!this.brand) {
        this.showNotification(
          "Seleccione un proveedor antes de crear una nueva orden",
          "error"
        );
        return;
      }

      // Find the selected supplier object
      const selectedSupplier = this.supplier_lst.find((s) => s.codigo === this.brand);

      if (selectedSupplier) {
        this.$router.push({
          name: "Order",
          params: { supplier: selectedSupplier },
        });
      } else {
        this.showNotification("Error al obtener información del proveedor", "error");
      }
    },

    showNotification(message, type = "info") {
      // Si tienes un sistema de notificaciones, úsalo aquí
      console.log(`[${type}] ${message}`);
      // Alternativamente, podrías usar un snackbar si está disponible en el componente
    },
  },

  watch: {
    selected: function () {
      this.SelectedCount = 0;
      this.selected.forEach((itm) => {
        this.SelectedCount += parseFloat(itm.quantity);
      });
    },
  },
};
</script>

<style scoped>
/* Overview Card Styles */
.overview-card {
  border: 1px solid #e0e0e0;
  background: linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%);
}

.overview-header {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.overview-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #333;
  margin: 0;
}

/* Compact KPI Styles */
.compact-kpi {
  background: white;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 12px;
  height: 100%;
  transition: all 0.2s ease;
}

.compact-kpi:hover {
  border-color: #d0d0d0;
  transform: translateY(-1px);
}

.compact-kpi.secondary {
  background: #fafafa;
  border-color: #f0f0f0;
}

.kpi-header {
  display: flex;
  align-items: center;
  margin-bottom: 4px;
}

.kpi-label {
  font-size: 0.75rem;
  font-weight: 500;
  color: #666;
  line-height: 1.2;
}

.kpi-label.small {
  font-size: 0.7rem;
}

.kpi-main-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #333;
  line-height: 1.1;
  margin: 2px 0;
}

.kpi-value-small {
  font-size: 1.25rem;
  font-weight: 600;
  color: #333;
  line-height: 1.1;
  margin: 2px 0;
}

.kpi-subtitle {
  font-size: 0.65rem;
  color: #999;
  line-height: 1.2;
}

.kpi-subtitle.small {
  font-size: 0.6rem;
}

/* Main Card - mantener estilos existentes */
.main-card {
  border: 1px solid #e0e0e0;
}

.minimal-toolbar {
  border-bottom: 1px solid #e0e0e0;
  padding: 16px 24px;
}

.section-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .compact-kpi {
    padding: 10px;
    margin-bottom: 8px;
  }

  .kpi-main-value {
    font-size: 1.25rem;
  }

  .kpi-value-small {
    font-size: 1.1rem;
  }

  .overview-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
}

@media (max-width: 600px) {
  .overview-card .v-card__text {
    padding: 16px !important;
  }

  .compact-kpi {
    padding: 8px;
  }
}
</style>
